import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@vitness/theme';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Tailwind/utility classes to size and shape the placeholder (e.g. `h-4 w-32`). */
  className?: string;
}

/**
 * Token-driven loading placeholder. Renders a pulsing block whose surface and
 * radius are driven by `--vn-*` tokens, so it adapts to theme, accent and
 * density automatically. Compose size/shape with utility classes via
 * `className` (e.g. `<Skeleton className="h-4 w-32" />`).
 *
 * `aria-hidden` by default — skeletons are purely decorative; announce the
 * loading state on the surrounding region instead (e.g. `aria-busy`).
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn('animate-pulse rounded-vn-md bg-surface-card-hover', className)}
      {...props}
    />
  );
});
